/**
 * ViewRegistry.ts - Central registry for all view definitions
 * 
 * ARCHITECTURE:
 * Singleton registry that stores all view definitions.
 * Apps register views at startup, Viewr retrieves them for rendering.
 */

import type {
  ViewrView,
  ViewMetadata,
  ViewRegistryEntry,
  ViewQueryOptions,
  ViewType,
} from '../types';
import { validateViewComplete } from './ViewValidator';

/**
 * Central view registry
 * Singleton pattern - one registry for entire application
 */
export class ViewRegistry {
  private static instance: ViewRegistry;
  private views: Map<string, ViewRegistryEntry>;

  private constructor() {
    this.views = new Map();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ViewRegistry {
    if (!ViewRegistry.instance) {
      ViewRegistry.instance = new ViewRegistry();
    }
    return ViewRegistry.instance;
  }

  /**
   * Register a view
   */
  public register(view: ViewrView, metadata?: Partial<ViewMetadata>): void {
    // Validate view
    const validation = validateViewComplete(view);
    if (!validation.valid) {
      throw new Error(`Invalid view definition for "${view.id}": ${validation.errors.join(', ')}`);
    }

    // Log warnings
    if (validation.warnings.length > 0) {
      console.warn(`View "${view.id}" has warnings:`, validation.warnings);
    }

    // Check for duplicate ID
    if (this.views.has(view.id)) {
      console.warn(`View "${view.id}" is already registered. Overwriting.`);
    }

    // Create registry entry
    const entry: ViewRegistryEntry = {
      view,
      metadata: {
        registeredAt: new Date(),
        registeredBy: metadata?.registeredBy || 'unknown',
        version: metadata?.version,
        builtin: metadata?.builtin || false,
      },
    };

    this.views.set(view.id, entry);
  }

  /**
   * Register multiple views
   */
  public registerMany(views: ViewrView[], metadata?: Partial<ViewMetadata>): void {
    views.forEach(view => this.register(view, metadata));
  }

  /**
   * Get a view by ID
   */
  public get(id: string): ViewrView | undefined {
    const entry = this.views.get(id);
    return entry?.view;
  }

  /**
   * Get view with metadata
   */
  public getEntry(id: string): ViewRegistryEntry | undefined {
    return this.views.get(id);
  }

  /**
   * Check if view exists
   */
  public has(id: string): boolean {
    return this.views.has(id);
  }

  /**
   * Get all views
   */
  public getAll(): ViewrView[] {
    return Array.from(this.views.values()).map(entry => entry.view);
  }

  /**
   * Get all entries with metadata
   */
  public getAllEntries(): ViewRegistryEntry[] {
    return Array.from(this.views.values());
  }

  /**
   * Query views with filters
   */
  public query(options: ViewQueryOptions): ViewrView[] {
    let results = this.getAll();

    // Filter by type
    if (options.type) {
      const types = Array.isArray(options.type) ? options.type : [options.type];
      results = results.filter(view => types.includes(view.type));
    }

    // Filter by data source
    if (options.dataSource) {
      const sources = Array.isArray(options.dataSource) 
        ? options.dataSource 
        : [options.dataSource];
      results = results.filter(view => sources.includes(view.dataSource));
    }

    // Filter by category
    if (options.category) {
      const categories = Array.isArray(options.category) 
        ? options.category 
        : [options.category];
      results = results.filter(view => 
        view.category && categories.includes(view.category)
      );
    }

    // Filter by tags
    if (options.tags) {
      const tags = Array.isArray(options.tags) ? options.tags : [options.tags];
      results = results.filter(view =>
        view.tags && tags.some(tag => view.tags!.includes(tag))
      );
    }

    // Filter by enabled status
    if (options.enabledOnly) {
      results = results.filter(view => view.enabled !== false);
    }

    // Search in name/description
    if (options.search) {
      const searchLower = options.search.toLowerCase();
      results = results.filter(view =>
        view.name.toLowerCase().includes(searchLower) ||
        (view.description && view.description.toLowerCase().includes(searchLower))
      );
    }

    return results;
  }

  /**
   * Get views by data source
   */
  public getByDataSource(dataSource: string): ViewrView[] {
    return this.query({ dataSource });
  }

  /**
   * Get views by category
   */
  public getByCategory(category: string): ViewrView[] {
    return this.query({ category });
  }

  /**
   * Get views by type
   */
  public getByType(type: ViewType): ViewrView[] {
    return this.query({ type });
  }

  /**
   * Get views by tag
   */
  public getByTag(tag: string): ViewrView[] {
    return this.query({ tags: tag });
  }

  /**
   * Unregister a view
   */
  public unregister(id: string): boolean {
    return this.views.delete(id);
  }

  /**
   * Clear all views
   */
  public clear(): void {
    this.views.clear();
  }

  /**
   * Get registry statistics
   */
  public getStats() {
    const all = this.getAll();
    const byType: Record<string, number> = {};
    const byDataSource: Record<string, number> = {};
    const byCategory: Record<string, number> = {};

    all.forEach(view => {
      // Count by type
      byType[view.type] = (byType[view.type] || 0) + 1;

      // Count by data source
      byDataSource[view.dataSource] = (byDataSource[view.dataSource] || 0) + 1;

      // Count by category
      if (view.category) {
        byCategory[view.category] = (byCategory[view.category] || 0) + 1;
      }
    });

    return {
      total: all.length,
      enabled: all.filter(v => v.enabled !== false).length,
      byType,
      byDataSource,
      byCategory,
    };
  }
}

/**
 * Export singleton instance for convenience
 */
export const viewRegistry = ViewRegistry.getInstance();
